import React from 'react';
import { Input } from '@/components/ui/input';
import { rupees } from './totals';
import { SectionHeading } from './DocumentChrome';

/**
 * The right-hand totals stack, and the words underneath it.
 *
 * Two things here are typed and everything else is computed: the GST rate and
 * the freight. The plate and die rows are tinted the colour of the box each one
 * came from, so a wrong charge is traced back to its table without reading the
 * labels.
 */
function Row({ label, children, className }) {
  return (
    <div className={`flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5 ${className || ''}`}>
      <span className="text-[11px] text-doc-muted">{label}</span>
      {children}
    </div>
  );
}

const value = 'font-mono text-[12px] font-medium tabular-nums text-doc-navy';

export default function SummaryPanel({ totals, gstRate, onGstRate, freight, onFreight, amountWords }) {
  return (
    <div>
      <SectionHeading>Summary</SectionHeading>
      <div className="overflow-hidden rounded border border-doc-rule">
        <Row label="Items subtotal">
          <span className={value} data-testid="summary-items-subtotal">{rupees(totals.items_subtotal)}</span>
        </Row>
        <Row label="GST">
          <span className="flex items-center gap-1.5">
            <Input
              type="number"
              value={gstRate ?? ''}
              onChange={(e) => onGstRate(e.target.value)}
              aria-label="GST rate percent"
              data-testid="gst-rate"
              className="h-6 w-14 rounded border-doc-rule px-1 text-center font-mono text-[11px] shadow-none"
            />
            <span className="text-[10px] text-doc-muted">% =</span>
            <span className={value} data-testid="summary-gst">{rupees(totals.gst_amount)}</span>
          </span>
        </Row>
        <Row label="Plate charges" className="bg-doc-teal/5">
          <span className={value} data-testid="summary-plate">{rupees(totals.plate_total)}</span>
        </Row>
        <Row label="Die charges" className="bg-doc-die/5">
          <span className={value} data-testid="summary-die">{rupees(totals.die_total)}</span>
        </Row>
        <Row label="Freight / Other">
          <Input
            type="number"
            value={freight ?? ''}
            onChange={(e) => onFreight(e.target.value)}
            placeholder="0.00"
            aria-label="Freight or other charges"
            data-testid="freight"
            className="h-6 w-24 rounded border-doc-rule px-1 text-right font-mono text-[11px] shadow-none"
          />
        </Row>
        <div className="flex items-center justify-between gap-2 bg-doc-navy px-3 py-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/65">Grand Total</span>
          <span className="font-mono text-[15px] font-bold tabular-nums text-doc-gold" data-testid="summary-grand-total">
            {rupees(totals.grand_total)}
          </span>
        </div>
      </div>

      <div className="mt-2.5 rounded border border-doc-gold bg-amber-50/60 px-3 py-1.5">
        <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-doc-gold">Amount in Words</span>
        <p className="text-[12px] font-semibold italic text-doc-navy" data-testid="amount-in-words">{amountWords}</p>
      </div>
    </div>
  );
}
